import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEnvelope, FaArrowLeft, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import axiosInstance from '../../../api/axiosInstance';
import logo from '../../../assets/Easymarg Whitebg.png';
import bgimg from '../../../assets/login.png'; 

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [loading, setLoading] = useState(false);

    // Block browser back button to login page
    const navigate = useNavigate();
    useEffect(() => {
        const pushStates = () => {
            for (let i = 0; i < 10; i++) {
                window.history.pushState(null, null, window.location.pathname);
            }
        };
        
        pushStates();
        
        const handlePopState = (e) => {
            pushStates();
            navigate('/forgot-password', { replace: true });
        };
        
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setMessageType('');

        try {
            const res = await axiosInstance.post('/users/forgot-password', { email }); 
            
            setMessage(res.data.message || 'Password reset link sent! Please check your email.');
            setMessageType('success');
            setEmail(''); // Clear email field on success

        } catch (error) {
            const errorMessage = 
                error.response?.data?.message || 
                'Error sending link. Please ensure the email is correct and try again.';
            
            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    return (
        <div className="h-screen w-full flex flex-col lg:flex-row overflow-hidden">
            {/* Left Brand Panel */}
            <div
                className="hidden lg:block relative w-full lg:w-1/2 lg:h-full shrink-0 bg-cover bg-center overflow-hidden"
                style={{ backgroundImage: `url(${bgimg})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a2463]/95 via-[#134DA7]/90 to-[#0a2463]/95 backdrop-blur-sm"></div>

                <div className="absolute top-5 left-5 sm:top-8 sm:left-8 z-20 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-lg">
                    <img src={logo} alt="EasyMarg Logo" className="h-6 sm:h-7" />
                </div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={staggerContainer}
                    className="relative z-10 h-full flex flex-col items-center lg:items-start justify-center text-white text-center lg:text-left px-6 sm:px-10 lg:pl-16 lg:pr-10 xl:pl-24"
                >
                    <motion.h1 variants={fadeInUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3 lg:mb-6">
                        WELCOME <br /> <span className="font-light">TO </span>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-200">EASYMARG</span>
                    </motion.h1>
                    <motion.p variants={fadeInUp} className="text-sm sm:text-lg font-light text-blue-100/90 max-w-lg mx-auto lg:mx-0">
                        Simplify your financial management with our innovative and secure platform.
                    </motion.p>
                </motion.div>
            </div>

            {/* Right Form Panel */}
            <div className="flex-1 min-h-0 flex flex-col items-center bg-gray-50 p-4 sm:p-8 lg:p-12">
                <div className="lg:hidden mb-6 mt-4 bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100">
                    <img src={logo} alt="EasyMarg Logo" className="h-7" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full max-w-[480px] max-h-full my-auto flex flex-col min-h-0"
                >
                    <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-3 px-1 shrink-0">
                        <span>EasyMarg</span>
                        <span className="text-gray-300">/</span>
                        <span className="text-[#134DA7] font-semibold">Forgot Password</span>
                    </div>

                    <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col min-h-0">
                        <div className="h-1.5 bg-gradient-to-r from-[#134DA7] to-[#66BBFC] shrink-0"></div>
                        
                        <div className="auth-scrollbar flex-1 min-h-0 overflow-y-auto p-6 sm:p-8">
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-[#0a2463]">Forgot Password?</h2>
                                <p className="text-sm text-gray-500 mt-1">No worries, we'll send you reset instructions</p>
                            </div>

                            {message && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 border ${
                                        messageType === 'success' 
                                            ? 'bg-green-50 text-green-700 border-green-200' 
                                            : 'bg-red-50 text-red-700 border-red-200'
                                    }`}
                                >
                                    {messageType === 'success' ? (
                                        <FaCheckCircle className="text-green-600 shrink-0 mt-0.5" />
                                    ) : (
                                        <FaExclamationCircle className="text-red-600 shrink-0 mt-0.5" />
                                    )}
                                    <span className="font-medium">{message}</span>
                                </motion.div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase ml-1">Email Address</label>
                                    <div className="relative mt-1 group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FaEnvelope className="text-gray-400 transition-colors group-focus-within:text-[#134DA7]" />
                                        </div>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                            required
                                            placeholder="name@example.com"
                                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#134DA7] outline-none transition-colors"
                                        />
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-2.5 text-white font-bold tracking-wide rounded-xl bg-[#134DA7] hover:bg-[#0f3d85] shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Sending Request...</span>
                                            </>
                                        ) : (
                                            'Send Reset Link'
                                        )}
                                    </button>
                                </div>
                            </form>

                            <div className="mt-6 text-center">
                                <Link 
                                    to="/" 
                                    className="inline-flex items-center gap-2 text-sm font-bold text-[#134DA7] hover:text-[#0f3d85] transition-colors group"
                                >
                                    <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                                    Back to Login
                                </Link>
                            </div>

                            <div className="mt-6 text-center text-[14px]">
                                <p className="text-gray-600 font-medium">Remember your password? <Link to="/" className="text-[#134DA7] font-bold underline hover:text-[#0f3d85]">Login here</Link></p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ForgotPassword;